import React, { useState } from 'react';
import { X, Clock, CheckCircle, XCircle, Download, ExternalLink } from 'lucide-react';

function SyntheticResultsModal({ test, onClose, darkMode = false }) {
  const { name, results = [] } = test;

  const [selectedResult, setSelectedResult] = useState(results[0] || null);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleString('tr-TR', { timeZone: 'UTC', dateStyle: 'short', timeStyle: 'short' }) + ' UTC';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 dark:text-green-400 flex-shrink-0" />;
      case 'failed': return <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 dark:text-red-400 flex-shrink-0" />;
      default: return <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'success': return darkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200';
      case 'failed': return darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200';
      default: return darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600 dark:text-green-400';
      case 'failed': return 'text-red-600 dark:text-red-400';
      default: return 'text-yellow-600 dark:text-yellow-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className={`rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] flex flex-col ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0 ${
          darkMode ? 'border-gray-700' : 'border-gray-100'
        }`}>
          <div className="min-w-0 flex-1 pr-2">
            <h2 className={`text-base sm:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Test Results: {name}
            </h2>
            <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {results.length} total runs
            </p>
          </div>
          <button 
            onClick={onClose} 
            className={`p-1.5 sm:p-2 rounded-xl transition-colors flex-shrink-0 ${
              darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
            }`}
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {/* Content - Mobilde dikey kaydırma, masaüstünde yatay */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {/* Results List - Mobilde yatay kaydırma ile kartlar */}
          <div className="mb-4 md:mb-0 md:hidden">
            <h3 className={`text-xs font-semibold mb-2 px-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Test Runs ({results.length})
            </h3>
            {results.length === 0 ? (
              <div className="text-center py-8">
                <Clock className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No results yet</p>
              </div>
            ) : (
              <div className="flex overflow-x-auto gap-2 pb-2 -mx-1 px-1">
                {results.map((result, idx) => (
                  <div
                    key={result.id || idx}
                    onClick={() => setSelectedResult(result)}
                    className={`flex-shrink-0 w-40 p-3 rounded-xl border cursor-pointer transition ${
                      selectedResult?.id === result.id
                        ? darkMode 
                          ? 'border-green-500 bg-green-900/30' 
                          : 'border-green-500 bg-green-50'
                        : darkMode 
                          ? 'border-gray-700 hover:border-green-700 hover:bg-gray-700/50' 
                          : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center space-x-1.5 min-w-0">
                        {getStatusIcon(result.status)}
                        <span className={`font-medium text-xs truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {result.status === 'success' ? 'Success' : result.status === 'failed' ? 'Failed' : 'Unknown'}
                        </span>
                      </div>
                      <span className={`text-[10px] flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {result.duration ? `${result.duration}ms` : '-'}
                      </span>
                    </div>
                    <div className={`mt-1.5 text-[10px] break-words ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {formatDate(result.startedAt)}
                    </div>
                    {result.error && (
                      <div className={`mt-1.5 text-[10px] truncate ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                        Error
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Sidebar - Masaüstünde solda göster */}
          <div className="hidden md:flex md:w-1/3 md:flex-col md:pr-4 md:border-r md:float-left md:mr-4">
            <div className="space-y-2 overflow-y-auto max-h-[60vh] pr-2">
              {results.length === 0 ? (
                <div className="text-center py-10">
                  <Clock className={`h-10 w-10 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No results yet</p>
                </div>
              ) : (
                results.map((result, idx) => (
                  <div
                    key={result.id || idx}
                    onClick={() => setSelectedResult(result)}
                    className={`p-3 rounded-xl border cursor-pointer transition ${
                      selectedResult?.id === result.id
                        ? darkMode 
                          ? 'border-green-500 bg-green-900/30' 
                          : 'border-green-500 bg-green-50'
                        : darkMode 
                          ? 'border-gray-700 hover:border-green-700 hover:bg-gray-700/50' 
                          : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center space-x-2 min-w-0">
                        {getStatusIcon(result.status)}
                        <span className={`font-medium text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {result.status === 'success' ? 'Success' : result.status === 'failed' ? 'Failed' : 'Unknown'}
                        </span>
                      </div>
                      <span className={`text-xs flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {result.duration ? `${result.duration}ms` : '-'}
                      </span>
                    </div>
                    <div className={`mt-1.5 text-xs break-words ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {formatDate(result.startedAt)}
                    </div>
                    {result.error && (
                      <div className={`mt-1.5 text-xs truncate ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                        Error: {result.error}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Selected Result Details */}
          <div className="md:overflow-hidden">
            {selectedResult ? (
              <div className="space-y-4">
                {/* Status Card */}
                <div className={`p-4 rounded-xl border ${getStatusBg(selectedResult.status)}`}>
                  <div className="flex items-center space-x-2 mb-3">
                    {getStatusIcon(selectedResult.status)}
                    <span className={`font-semibold text-sm sm:text-base ${getStatusTextColor(selectedResult.status)}`}>
                      {selectedResult.status === 'success' ? 'Success' : selectedResult.status === 'failed' ? 'Failed' : 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-2 text-xs sm:text-sm">
                    <div className="flex flex-wrap items-baseline gap-1">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Started:</span>
                      <span className={`break-words ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatDate(selectedResult.startedAt)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-baseline gap-1">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Completed:</span>
                      <span className={`break-words ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatDate(selectedResult.completedAt)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-baseline gap-1">
                      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Duration:</span>
                      <span className={`break-words ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {selectedResult.duration ? `${selectedResult.duration}ms` : '-'}
                      </span>
                    </div>
                  </div>
                  
                  {selectedResult.error && (
                    <div className={`mt-3 p-2 rounded-lg text-xs break-words ${
                      darkMode ? 'bg-red-900/30 border border-red-800 text-red-300' : 'bg-red-100 border border-red-300 text-red-800'
                    }`}>
                      <strong>Error:</strong> {selectedResult.error}
                    </div>
                  )}
                </div>

                {/* Screenshot Section */}
                {selectedResult.screenshotPath ? (
                  <div>
                    <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      Screenshot
                    </h3>
                    <div className={`border rounded-lg overflow-hidden ${
                      darkMode ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                      <img
                        src={`${process.env.REACT_APP_API_URL || ''}${selectedResult.screenshotPath}`}
                        alt="Test screenshot"
                        className="w-full object-contain max-h-[250px] sm:max-h-[350px] md:max-h-[400px]"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <a
                        href={`${process.env.REACT_APP_API_URL || ''}${selectedResult.screenshotPath}`}
                        download
                        className={`flex items-center justify-center space-x-1 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                          darkMode 
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Download className="h-3.5 w-3.5" />
                        <span>Download</span>
                      </a>
                      <a
                        href={`${process.env.REACT_APP_API_URL || ''}${selectedResult.screenshotPath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-center space-x-1 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                          darkMode 
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        <span>Open</span>
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className={`p-4 rounded-lg text-center text-xs ${
                    darkMode ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-50 text-gray-500'
                  }`}>
                    No screenshot available for this run.
                  </div>
                )}
              </div>
            ) : (
              <div className={`text-center py-12 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Select a result to view details
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SyntheticResultsModal;